import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Target, 
  Crosshair, 
  Compass, 
  ArrowRight, 
  Brain,
  BookOpen,
  Video,
  Headphones,
  AlertTriangle
} from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db, collections } from '@/lib/firebase';

export function GrowView() {
  const [showMore, setShowMore] = useState(false);
  const { currentUser } = useAuth();
  const [growData, setGrowData] = useState<any>(null);
  const [supportData, setSupportData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadGrowData() {
      if (!currentUser) return;

      try {
        const reportRef = doc(db, collections.reports, currentUser.uid);
        const reportDoc = await getDoc(reportRef);

        if (reportDoc.exists()) {
          const data = reportDoc.data();
          if (data.grow_view_support_framework) {
            setGrowData(JSON.parse(data.grow_view_support_framework));
          }
          if (data.grow_view_support_resources) {
            setSupportData(JSON.parse(data.grow_view_support_resources));
          }
        }
      } catch (error) {
        console.error('Error loading GROW framework data:', error);
      } finally {
        setLoading(false);
      }
    }

    loadGrowData();
  }, [currentUser]);

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!growData) {
    return (
      <div className="p-6">
        <div className="bg-gray-50 rounded-xl p-8 text-center">
          <div className="flex justify-center mb-4">
            <AlertTriangle className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            GROW Framework Data Not Available
          </h3>
          <p className="text-gray-600 max-w-lg mx-auto">
            The GROW framework analysis will be available once your report is generated.
          </p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-6"
    >
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">GROW Strategy</h2>
      </div>

      <div className="mb-12 bg-[#f9fafb] rounded-xl p-6">
        <p className="text-gray-600 leading-relaxed mb-4">
          The GROW framework is a structured approach to achieving your goals, focusing on Goals, Reality, Options, and Way Forward. It provides actionable insights to help you understand your current state, explore possibilities, and define a clear action plan to move ahead effectively.
        </p>
        
        <motion.div
          initial={false}
          animate={{ height: showMore ? 'auto' : 0, opacity: showMore ? 1 : 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          <div className="space-y-6 text-gray-600 leading-relaxed mb-4">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Goals</h4>
              <p>Define your aspirations and identify key areas for growth. Based on personalized feedback:</p>
              <ul className="mt-2 ml-4 space-y-2">
                <li>Enhance leadership presence and team motivation skills.</li>
                <li>Develop more effective communication strategies.</li>
                <li>Balance technical excellence with interpersonal effectiveness.</li>
              </ul>
              <p className="mt-2">This step helps you clarify what success looks like and sets the foundation for actionable planning.</p>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Reality</h4>
              <p>Assess your current state to identify strengths and improvement areas. Insights from the feedback include:</p>
              <ul className="mt-2 ml-4 space-y-2">
                <li>Strong technical skills, but room for growth in soft skills.</li>
                <li>Communication style can sometimes be perceived as too assertive.</li>
                <li>Team values your expertise but seeks more emotional engagement.</li>
              </ul>
              <p className="mt-2">Understanding these realities ensures your strategies are grounded in practical self-awareness.</p>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Options</h4>
              <p>Explore actionable strategies to overcome challenges and leverage opportunities. Suggested approaches:</p>
              <ul className="mt-2 ml-4 space-y-2">
                <li>Enroll in leadership development programs.</li>
                <li>Practice active listening and emotional intelligence techniques.</li>
                <li>Seek mentorship opportunities for guided development.</li>
              </ul>
              <p className="mt-2">This phase broadens your perspective, empowering you to make informed decisions.</p>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Way Forward</h4>
              <p>Commit to a clear plan of action to achieve your goals:</p>
              <ul className="mt-2 ml-4 space-y-2">
                <li><strong>Short-term goals:</strong> Schedule weekly team check-ins, document and share knowledge, and set monthly leadership development objectives.</li>
                <li><strong>Long-term goals:</strong> Develop a comprehensive leadership roadmap, establish mentorship programs, and track emotional intelligence growth.</li>
              </ul>
              <p className="mt-2">This step transforms insights into tangible steps that align with your professional ambitions, ensuring sustainable progress.</p>
            </div>
          </div>
        </motion.div>
        
        <button
          onClick={() => setShowMore(!showMore)}
          className="text-primary hover:text-primary-hover font-medium flex items-center space-x-1"
        >
          <span>{showMore ? 'Read less' : 'Read more'}</span>
          <motion.svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            animate={{ rotate: showMore ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <path
              d="M5 7.5L10 12.5L15 7.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </motion.svg>
        </button>
      </div>
      
      {/* Timeline Content */}
      <h3 className="text-xl font-semibold text-gray-900 mb-6">GROW Framework Implementation</h3>
      
      <div className="relative">
        {/* Timeline Line */}
        <div className="absolute left-12 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary via-secondary via-[#f9c55f] to-green-500" />

        {/* Goals Section */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="relative mb-16 pl-32"
        >
          <div className="absolute left-12 top-1/2 -translate-y-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-white border-2 border-primary flex items-center justify-center">
            <Target className="w-7 h-7 text-primary" strokeWidth={2} />
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">Goals</h3>
            <div className="space-y-3">
              <p className="text-gray-600 font-medium">Based on your feedback:</p>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <span className="text-primary mt-1.5">•</span>
                  <span className="text-gray-600">Enhance leadership presence and team motivation skills</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-primary mt-1.5">•</span>
                  <span className="text-gray-600">Develop more effective communication strategies</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-primary mt-1.5">•</span>
                  <span className="text-gray-600">Balance technical excellence with interpersonal effectiveness</span>
                </li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Reality Section */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="relative mb-16 pl-32"
        >
          <div className="absolute left-12 top-1/2 -translate-y-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-white border-2 border-secondary flex items-center justify-center">
            <Crosshair className="w-7 h-7 text-secondary" strokeWidth={2} />
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 bg-gradient-to-r from-secondary to-primary bg-clip-text text-transparent">Reality</h3>
            <div className="space-y-3">
              <p className="text-gray-600 font-medium">Current situation:</p>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <span className="text-secondary mt-1.5">•</span>
                  <span className="text-gray-600">Strong technical skills but room for growth in soft skills</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-secondary mt-1.5">•</span>
                  <span className="text-gray-600">Communication style sometimes perceived as too assertive</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-secondary mt-1.5">•</span>
                  <span className="text-gray-600">Team values your expertise but seeks more emotional engagement</span>
                </li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Options Section */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="relative mb-16 pl-32"
        >
          <div className="absolute left-12 top-1/2 -translate-y-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-white border-2 border-[#f9c55f] flex items-center justify-center">
            <Compass className="w-7 h-7 text-[#f9c55f]" strokeWidth={2} />
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 bg-gradient-to-r from-[#f9c55f] to-secondary bg-clip-text text-transparent">Options</h3>
            <div className="space-y-3">
              <p className="text-gray-600 font-medium">Potential approaches:</p>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <span className="text-[#f9c55f] mt-1.5">•</span>
                  <span className="text-gray-600">Enroll in leadership development programs</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-[#f9c55f] mt-1.5">•</span>
                  <span className="text-gray-600">Practice active listening and emotional intelligence techniques</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-[#f9c55f] mt-1.5">•</span>
                  <span className="text-gray-600">Seek mentorship opportunities</span>
                </li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Way Forward Section */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="relative pl-32"
        >
          <div className="absolute left-12 top-1/2 -translate-y-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-white border-2 border-green-500 flex items-center justify-center">
            <ArrowRight className="w-7 h-7 text-green-500" strokeWidth={2} />
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 bg-gradient-to-r from-green-500 to-[#f9c55f] bg-clip-text text-transparent">Way Forward</h3>
            <div className="space-y-3">
              <div className="space-y-6">
                <div>
                  <p className="text-gray-600 font-medium mb-3">Short-term Action Plan:</p>
                  <ul className="space-y-3"> 
                    {growData?.wayForward?.shortTerm?.[0]?.items.map((item, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <span className="text-green-600 mt-1.5">•</span>
                        <span className="text-gray-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <p className="text-gray-600 font-medium mb-3">Long-term Action Plan:</p>
                  <ul className="space-y-3">
                    {growData?.wayForward?.longTerm?.[0]?.items.map((item, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <span className="text-green-600 mt-1.5">•</span>
                        <span className="text-gray-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Support Resources */}
      {!supportData ? (
        <div className="mt-16 bg-gray-50 rounded-xl p-8 text-center">
          <div className="flex justify-center mb-4">
            <AlertTriangle className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Support Resources Not Available
          </h3>
          <p className="text-gray-600 max-w-lg mx-auto">
            Support resources will be available once your report is generated.
          </p>
        </div>
      ) : (
        <div className="mt-16 bg-gradient-to-br from-primary/5 via-secondary/5 to-primary/5 rounded-xl p-8">
        <div className="text-center mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Support Resources</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Curated resources to help you on your leadership journey. Each recommendation is carefully selected to align with your growth areas.
          </p>
        </div>
        
        {/* Key Concepts */}
        <div className="mb-12">
          <div className="flex items-center justify-center space-x-2 mb-6">
            <Brain className="w-6 h-6 text-primary" />
            <h3 className="text-lg font-semibold text-gray-900">Key Concepts to Deep Dive</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {supportData.keyConcepts.map((concept: any, index: number) => (
              <a 
                key={index}
                href={concept.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white/50 backdrop-blur-sm rounded-lg p-6 shadow-sm border border-gray-200 hover:shadow-md hover:scale-[1.02] transition-all duration-200"
              >
                <h4 className="font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors">{concept.title}</h4>
                <p className="text-sm text-gray-600">{concept.description}</p>
                <div className="mt-4 text-primary text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  Learn more →
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* Recommended Books */}
        <div className="mb-12">
          <div className="flex items-center justify-center space-x-2 mb-6">
            <BookOpen className="w-6 h-6 text-secondary" />
            <h3 className="text-lg font-semibold text-gray-900">Recommended Books</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {supportData.recommendedBooks.map((book: any, index: number) => (
              <a 
                key={index}
                href={book.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white/50 backdrop-blur-sm rounded-lg p-6 shadow-sm border border-gray-200 hover:shadow-md hover:scale-[1.02] transition-all duration-200"
              >
                <h4 className="font-semibold text-gray-900 mb-2 group-hover:text-secondary transition-colors">{book.title}</h4>
                <p className="text-sm text-gray-600">{book.description}</p>
                <div className="mt-4 text-secondary text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  View on Amazon →
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* TED Talks */}
        <div className="mb-12">
          <div className="flex items-center justify-center space-x-2 mb-6">
            <Video className="w-6 h-6 text-[#f9c55f]" />
            <h3 className="text-lg font-semibold text-gray-900">Inspiring TED Talks</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {supportData.tedTalks.map((talk: any, index: number) => (
              <a 
                key={index}
                href={talk.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white/50 backdrop-blur-sm rounded-lg p-6 shadow-sm border border-gray-200 hover:shadow-md hover:scale-[1.02] transition-all duration-200"
              >
                <h4 className="font-semibold text-gray-900 mb-2 group-hover:text-[#f9c55f] transition-colors">{talk.title}</h4>
                <p className="text-sm text-gray-600">{talk.description}</p>
                <div className="mt-4 text-[#f9c55f] text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  Watch on TED →
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* Podcasts */}
        <div>
          <div className="flex items-center justify-center space-x-2 mb-6">
            <Headphones className="w-6 h-6 text-green-500" />
            <h3 className="text-lg font-semibold text-gray-900">Recommended Podcasts</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {supportData.podcasts.map((podcast: any, index: number) => (
              <a 
                key={index}
                href={podcast.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white/50 backdrop-blur-sm rounded-lg p-6 shadow-sm border border-gray-200 hover:shadow-md hover:scale-[1.02] transition-all duration-200"
              >
                <h4 className="font-semibold text-gray-900 mb-2 group-hover:text-green-500 transition-colors">{podcast.title}</h4>
                <p className="text-sm text-gray-600">{podcast.description}</p>
                <div className="mt-4 text-green-500 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  Listen on Apple Podcasts →
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>)}
    </motion.div>
  );
}